import { useState, useEffect } from "react";
import { getProjects } from "../services/projectService";

interface ApiError {
  message?: string;
}

interface RawDraw {
  id: string | null;
  lastDrawNumber: number | null;
  status: string | null;
}

interface RawProject {
  project?: RawProjectCore;
  id?: string;
  projectId?: string;
  _id?: string;
  name?: string;
  type?: string;
  status?: string;
  projectStage?: string;
  progressPercentage?: number;
  progress?: number;
  createdAt?: string;
  address?: string;
  addressCoordinate?: { latitude: number; longitude: number } | null;
  coordinates?: { lat: number; lon: number };
  stakeholders?: {
    lenderBranch?: { companyName?: string };
    lender?: { companyName?: string };
  };
  preConstructionReport?: { estCompletionDate?: string };
  drawRequest?: RawDraw;
}

interface RawProjectCore extends Omit<RawProject, "project"> {}

export interface AdminProject {
  id: string;
  name: string;
  type: string;
  status: string;
  projectStage: string;
  progress: number;
  bank: string;
  createdAt: string;
  estCompletionDate: string;
  drawId: string;
  lastDrawNumber: number;
  address: string;
  coordinates?: { lat: number; lon: number };
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProjectFilters {
  status: string;
  type: string;
  lenderCompanyName: string;
}

const STATUS_DISPLAY: Record<string, string> = {
  active: "Active",
  inActive: "Inactive",
  pre_construction: "Pre-construction",
  draw_request: "Draw Request",
  completed: "Completed",
  cancelled: "Cancelled",
};

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

const normalizeProject = (p: RawProject): AdminProject => {
  const proj: RawProjectCore = p.project ?? p;
  return {
    id: String(proj.id ?? proj.projectId ?? proj._id ?? ""),
    drawId: p.drawRequest?.id ?? "",
    name: proj.name ?? "",
    type: capitalize(proj.type ?? ""),
    status: STATUS_DISPLAY[proj.status ?? ""] ?? capitalize(proj.status ?? ""),
    projectStage: proj.projectStage ?? "",
    progress: parseFloat(String(proj.progressPercentage ?? proj.progress ?? 0)),
    bank:
      proj.stakeholders?.lenderBranch?.companyName ??
      proj.stakeholders?.lender?.companyName ??
      "",
    createdAt: proj.createdAt ?? "",
    estCompletionDate: proj.preConstructionReport?.estCompletionDate ?? "",
    lastDrawNumber: proj?.drawRequest?.lastDrawNumber || 0,
    address: proj.address ?? "",
    coordinates: proj.addressCoordinate
      ? { lat: proj.addressCoordinate.latitude, lon: proj.addressCoordinate.longitude }
      : proj.coordinates,
  };
};

const useGetAdminProjects = (
  page = 1,
  limit = 10,
  search = "",
  sortBy = "createdAt",
  sortOrder = "DESC",
  filters: ProjectFilters = { status: "", type: "", lenderCompanyName: "" },
  enabled = true,
) => {
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  const refetch = () => setTick((t) => t + 1);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();

    const fetchProjects = async () => {
      setLoading(true);
      setError("");

      const params = {
        page,
        limit,
        sortBy,
        sortOrder,
        ...(search.trim() && { q: search.trim() }),
        ...(filters.status && { status: filters.status }),
        ...(filters.type && { type: filters.type }),
        ...(filters.lenderCompanyName && {
          lenderCompanyName: filters.lenderCompanyName,
        }),
      };

      const { data, error: apiError } = (await getProjects(params)) as {
        data: {
          data?: RawProject[];
          projects?: RawProject[];
          pagination?: Pagination;
        } | null;
        error: ApiError | null;
      };

      if (controller.signal.aborted) return;

      if (apiError) {
        setError(apiError.message || "Failed to load projects.");
        setLoading(false);
        return;
      }

      const list = data?.data ?? data?.projects ?? [];
      setProjects(list.map(normalizeProject));
      setPagination(
        data?.pagination ?? { total: list.length, page, limit, totalPages: 1 },
      );
      setLoading(false);
    };

    fetchProjects();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    filters.status,
    filters.type,
    filters.lenderCompanyName,
    tick,
  ]);

  return { projects, pagination, loading, error, refetch };
};

export default useGetAdminProjects;
